import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { RATE_EMAIL_PRESETS, resolveEmailPresetValue } from '../email-presets';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

export interface EmailTextSelectProps {
  /** Current email_custom_text value (preset text or freeform). */
  value: string | null;
  /** Called with the new email_custom_text. `null` clears the field. */
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** sm = inline row cell, md = drawer / form. Default 'sm'. */
  size?: 'sm' | 'md';
  /** Optional aria-label override. */
  ariaLabel?: string;
}

/**
 * Inline select for the rate/invoice "extra email text" field. Backs to
 * one of the canonical RATE_EMAIL_PRESETS or to a freeform string via
 * the "Andet (skriv selv)" option, which opens a small textarea
 * popover.
 *
 * Why a popover and not a separate textarea field: Daniel wanted the
 * picker to live ON the row alongside the status select, with the same
 * UX. The textarea only appears when the user actively chooses "Andet"
 * — saving to email_custom_text on blur, discarding on ESC.
 */
export function EmailTextSelect({
  value,
  onChange,
  disabled,
  size = 'sm',
  ariaLabel,
}: EmailTextSelectProps) {
  const selectedKey = resolveEmailPresetValue(value);
  const [showOther, setShowOther] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    if (showOther) {
      // Defer focus until popover is rendered.
      const id = window.setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }, 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [showOther]);

  useOnClickOutside(
    wrapperRef,
    () => {
      if (!showOther) return;
      // Save on click-outside (same behaviour as blur).
      const trimmed = draft.trim();
      onChange(trimmed === '' ? null : draft);
      setShowOther(false);
    },
    showOther,
  );

  function handleSelectChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const key = e.target.value;
    if (key === '') {
      onChange(null);
      setShowOther(false);
      return;
    }
    if (key === '__OTHER__') {
      setDraft(value ?? '');
      setShowOther(true);
      return;
    }
    const preset = RATE_EMAIL_PRESETS.find((p) => p.value === key);
    if (preset) {
      onChange(preset.text);
      setShowOther(false);
    }
  }

  function commitDraft() {
    const trimmed = draft.trim();
    onChange(trimmed === '' ? null : draft);
    setShowOther(false);
  }

  function handleTextareaKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value ?? '');
      setShowOther(false);
    }
  }

  const sizeCls = size === 'md' ? 'h-9 px-2 text-sm' : 'h-7 px-1 text-xs';

  // Truncated preview of the freeform value, shown to the right of the
  // select when "Andet" is the currently selected option.
  const showFreeformPreview = selectedKey === '__OTHER__' && !showOther && value;

  return (
    <div ref={wrapperRef} className="relative inline-flex items-center gap-1.5">
      <select
        value={selectedKey}
        onChange={handleSelectChange}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Email-tekst'}
        className={cn(
          'rounded border border-line-2 bg-paper-0 text-ink-0',
          'focus:outline-none focus-visible:border-primary',
          'disabled:bg-paper-1 disabled:text-ink-3 disabled:cursor-not-allowed',
          sizeCls,
        )}
      >
        <option value="">(ingen)</option>
        {RATE_EMAIL_PRESETS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>

      {showFreeformPreview ? (
        <span className="max-w-[14ch] truncate text-xs text-ink-2" title={value ?? undefined}>
          {value}
        </span>
      ) : null}

      {showOther ? (
        <div
          className={cn(
            'absolute left-0 top-full z-20 mt-1 w-72',
            'rounded border border-line-2 bg-paper-0 p-2 shadow-2',
          )}
        >
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={handleTextareaKey}
            rows={3}
            placeholder="Skriv din egen tekst…"
            className="w-full rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm text-ink-0 focus:outline-none focus-visible:border-primary"
          />
          <div className="mt-1 text-[11px] text-ink-2">
            Tab eller klik væk for at gemme · Esc for at fortryde
          </div>
        </div>
      ) : null}
    </div>
  );
}
